import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getCrmLead, updateCrmLead, type CrmLead, type CrmLeadStatus } from "../../api";

function toDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function CrmLeadDetail() {
  const { leadId } = useParams<{ leadId: string }>();
  const nav = useNavigate();
  const [lead, setLead] = useState<CrmLead | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [status, setStatus] = useState<CrmLeadStatus>("active");
  const [nextStepAt, setNextStepAt] = useState("");
  const [nextStepLabel, setNextStepLabel] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    getCrmLead(leadId)
      .then((l) => {
        if (cancelled) return;
        setLead(l);
        setFirstName(l.first_name);
        setLastName(l.last_name || "");
        setEmail(l.email || "");
        setCompany(l.company || "");
        setStatus(l.status);
        setNextStepAt(toDatetimeLocalValue(l.next_step_at));
        setNextStepLabel(l.next_step_label || "");
        setNotes(l.notes || "");
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Lead introuvable");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !firstName.trim()) return;
    setSaving(true);
    setErr(null);
    try {
      let nextIso: string | null = null;
      if (nextStepAt.trim()) {
        const d = new Date(nextStepAt);
        nextIso = Number.isNaN(d.getTime()) ? null : d.toISOString();
      }
      const updated = await updateCrmLead(leadId, {
        first_name: firstName.trim(),
        last_name: lastName.trim() || null,
        email: email.trim() || null,
        company: company.trim() || null,
        status,
        next_step_at: nextIso,
        next_step_label: nextStepLabel.trim() || null,
        notes: notes.trim() || null,
      });
      setLead(updated);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-concrete">Chargement…</p>;
  }

  if (err && !lead) {
    return (
      <div className="space-y-4">
        <p className="text-red-700">{err}</p>
        <button
          type="button"
          onClick={() => nav("/admin/crm/leads")}
          className="text-sm text-charcoal underline"
        >
          Retour à la liste
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <Link
          to="/admin/crm/leads"
          className="font-mono text-xs text-concrete hover:text-charcoal"
        >
          ← Leads
        </Link>
        {lead && (
          <span className="font-mono text-[11px] text-concrete">
            ID {lead.lead_id} · créé {new Date(lead.created_at).toLocaleString()}
          </span>
        )}
      </div>

      {err && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {err}
        </div>
      )}

      <form
        onSubmit={save}
        className="max-w-2xl space-y-4 rounded-xl border border-stone bg-white p-6 shadow-sm"
      >
        <h1 className="font-sans text-2xl font-bold tracking-tight text-black">
          Fiche lead
        </h1>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="text-concrete">Prénom *</span>
            <input
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="mt-1 w-full rounded-lg border border-stone px-3 py-2"
            />
          </label>
          <label className="block text-sm">
            <span className="text-concrete">Nom</span>
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="mt-1 w-full rounded-lg border border-stone px-3 py-2"
            />
          </label>
          <label className="block text-sm">
            <span className="text-concrete">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-lg border border-stone px-3 py-2"
            />
          </label>
          <label className="block text-sm">
            <span className="text-concrete">Entreprise</span>
            <input
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className="mt-1 w-full rounded-lg border border-stone px-3 py-2"
            />
          </label>
          <label className="block text-sm">
            <span className="text-concrete">Statut</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as CrmLeadStatus)}
              className="mt-1 w-full rounded-lg border border-stone px-3 py-2"
            >
              <option value="new">Nouveau</option>
              <option value="active">Actif</option>
              <option value="exited">Sorti</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-concrete">Prochaine action (date/heure)</span>
            <input
              type="datetime-local"
              value={nextStepAt}
              onChange={(e) => setNextStepAt(e.target.value)}
              className="mt-1 w-full rounded-lg border border-stone px-3 py-2"
            />
          </label>
          <label className="block text-sm sm:col-span-2">
            <span className="text-concrete">Libellé prochaine étape</span>
            <input
              value={nextStepLabel}
              onChange={(e) => setNextStepLabel(e.target.value)}
              className="mt-1 w-full rounded-lg border border-stone px-3 py-2"
            />
          </label>
          <label className="block text-sm sm:col-span-2">
            <span className="text-concrete">Notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={5}
              className="mt-1 w-full rounded-lg border border-stone px-3 py-2"
            />
          </label>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="rounded-lg bg-charcoal px-4 py-2 text-sm font-medium text-ivory disabled:opacity-50"
        >
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </form>
    </div>
  );
}
